import { FieldLayerWellsGroup } from '@/api/fields';
import { flattenFieldWells } from '@/utils/fieldWells';

export interface ActiveWellsYearPoint {
  year: number;
  activeWells: number;
}

export interface HistoricalProductionPoint {
  year: number;
  oil: number | null;
  gas: number | null;
  liquid: number | null;
  waterCut: number | null;
}

export interface DefaultProfilePoint {
  year: number;
  oil: number | null;
  gas: number | null;
  liquid: number | null;
  waterCut: number | null;
  water?: number | null;
}

export interface DefaultProfileCoefs {
  q0: number | null;
  a: number | null;
  wc0: number | null;
  k: number | null;
  q0_gas?: number | null;
  a_gas?: number | null;
}

/** Default horizon matches backend DECLINE_CURVE_HORIZON_YEARS. */
export const DEFAULT_PROFILE_HORIZON_YEARS = 50;

/**
 * Build default-well curves from coefficients.
 * oil: q0 * exp(-a * t)
 * gas: q0_gas * exp(-a_gas * t)
 * x = 1 - exp(-a * t)
 * WC: 1 - (1 - wc0) * (1 - x)^k  (needs oil decline)
 * liquid: oil / (1 - WC)
 */
export function buildDefaultProfilesFromCoefs(
  coefs: DefaultProfileCoefs,
  nYears: number = DEFAULT_PROFILE_HORIZON_YEARS,
): DefaultProfilePoint[] {
  if (nYears <= 0) {
    return [];
  }

  const q0 = coefs.q0;
  const a = coefs.a;
  const q0Gas = coefs.q0_gas ?? null;
  const aGas = coefs.a_gas ?? null;

  const hasOil = q0 != null && a != null && Number.isFinite(q0) && Number.isFinite(a)
    && q0 > 0 && a >= 0;
  const hasGas = q0Gas != null && aGas != null && Number.isFinite(q0Gas) && Number.isFinite(aGas)
    && q0Gas > 0 && aGas >= 0;
  if (!hasOil && !hasGas) {
    return [];
  }

  const hasWc = hasOil && coefs.wc0 != null && coefs.k != null
    && Number.isFinite(coefs.wc0) && Number.isFinite(coefs.k);

  const points: DefaultProfilePoint[] = [];
  for (let t = 0; t < nYears; t += 1) {
    const oil = hasOil ? (q0 as number) * Math.exp(-(a as number) * t) : null;
    const gas = hasGas ? (q0Gas as number) * Math.exp(-(aGas as number) * t) : null;

    if (!hasWc || oil == null) {
      points.push({
        year: t,
        oil,
        gas,
        liquid: null,
        waterCut: null,
        water: null,
      });
      continue;
    }

    const wc0 = Math.min(Math.max(coefs.wc0 as number, 0), 0.95);
    const k = Math.min(Math.max(coefs.k as number, 0.1), 1);
    const x = Math.min(Math.max(1 - Math.exp(-(a as number) * t), 0), 0.9999);
    let wc = 1 - (1 - wc0) * (1 - x) ** k;
    wc = Math.min(Math.max(wc, 0.001), 0.999);
    const liquid = oil / (1 - wc);
    const water = Math.max(liquid - oil, 0);
    points.push({
      year: t,
      oil,
      gas,
      liquid,
      waterCut: 100 * wc,
      water,
    });
  }
  return points;
}


export interface CombinedHistoryChartPoint {
  year: number;
  activeWells: number | null;
  oil: number | null;
  gas: number | null;
  liquid: number | null;
  waterCut: number | null;
}

export function mergeHistoryChartData(
  activeWells: ActiveWellsYearPoint[],
  production: HistoricalProductionPoint[],
): CombinedHistoryChartPoint[] {
  const byYear = new Map<number, CombinedHistoryChartPoint>();

  activeWells.forEach((row) => {
    byYear.set(row.year, {
      year: row.year,
      activeWells: row.activeWells,
      oil: null,
      gas: null,
      liquid: null,
      waterCut: null,
    });
  });

  production.forEach((row) => {
    const existing = byYear.get(row.year) ?? {
      year: row.year,
      activeWells: null,
      oil: null,
      gas: null,
      liquid: null,
      waterCut: null,
    };
    byYear.set(row.year, {
      ...existing,
      oil: row.oil,
      gas: row.gas,
      liquid: row.liquid,
      waterCut: row.waterCut,
    });
  });

  return [...byYear.values()].sort((a, b) => a.year - b.year);
}

/** Liquid = oil + water */
export function computeLiquid(oil: number | null, water: number | null): number | null {
  if (oil == null && water == null) {
    return null;
  }
  return (oil || 0) + (water || 0);
}

/** WC (%) = 100 * water / (water + oil) */
export function computeWaterCut(oil: number | null, water: number | null): number | null {
  if (water == null) {
    return null;
  }
  const total = (oil || 0) + water;
  if (total <= 0) {
    return null;
  }
  return (water / total) * 100;
}

export function formatThousandGasTooltip(value: number): string {
  return `${Number(value).toFixed(2)} thousand t`;
}

export interface FieldProductionChartPoint {
  year: number;
  oil: number | null;
  gas: number | null;
  liquid: number | null;
  waterCut: number | null;
}

export function buildFieldProductionChartData(
  productionData: Array<{ year: number; oil?: number | null; gas?: number | null; water?: number | null }>,
): FieldProductionChartPoint[] {
  return productionData.map((row) => ({
    year: row.year,
    oil: row.oil ?? null,
    gas: row.gas ?? null,
    liquid: computeLiquid(row.oil ?? null, row.water ?? null),
    waterCut: computeWaterCut(row.oil ?? null, row.water ?? null),
  }));
}

export function buildActiveWellsByYear(
  wellsGroups: FieldLayerWellsGroup[],
  layerId: string,
  calendarYears: number[],
): ActiveWellsYearPoint[] {
  const layerWells = flattenFieldWells(wellsGroups).filter((well) => well.layer_id === layerId);
  if (calendarYears.length === 0) {
    return [];
  }

  const entryYears = layerWells
    .map((well) => (well.entry_date ? new Date(well.entry_date).getFullYear() : null))
    .filter((year): year is number => year != null);

  const years =
    calendarYears.length > 0
      ? [...calendarYears].sort((a, b) => a - b)
      : [...new Set(entryYears)].sort((a, b) => a - b);

  if (years.length === 0) {
    return [];
  }

  return years.map((year) => ({
    year,
    activeWells: layerWells.filter((well) => {
      if (!well.entry_date) {
        return false;
      }
      const entryYear = new Date(well.entry_date).getFullYear();
      if (entryYear > year) {
        return false;
      }
      if (well.shut_down_date) {
        const shutDownYear = new Date(well.shut_down_date).getFullYear();
        if (shutDownYear <= year) {
          return false;
        }
      }
      return true;
    }).length,
  }));
}

export function collectCalendarYears(
  productionYears: number[],
  wellsGroups: FieldLayerWellsGroup[],
  layerId: string,
): number[] {
  const years = new Set<number>(productionYears);
  flattenFieldWells(wellsGroups)
    .filter((well) => well.layer_id === layerId && well.entry_date)
    .forEach((well) => {
      years.add(new Date(well.entry_date!).getFullYear());
    });
  return [...years].sort((a, b) => a - b);
}
